import React,{useEffect,useMemo,useRef,useState} from 'react';
import {supabase} from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {startStripeSellerOnboarding} from '../../services/stripePaymentService';
import SellerDisputesPanel from './SellerDisputesPanel';

const PAGE_SIZE=10;

const {
  FiAlertCircle,
  FiArrowRight,
  FiCheckCircle,
  FiCreditCard,
  FiDownload,
  FiDollarSign,
  FiExternalLink,
  FiInfo,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiTrendingUp,
  FiXCircle
}=FiIcons;

const money=(value)=>`$${Number(value || 0).toLocaleString(undefined,{
  minimumFractionDigits:2,
  maximumFractionDigits:2
})}`;

const csvCell=(value)=>`"${String(value ?? '').replaceAll('"','""')}"`;

const downloadCsv=(name,rows)=>{
  if (!rows.length) return;

  const headers=Object.keys(rows[0]);
  const csv=[
    headers.map(csvCell).join(','),
    ...rows.map((row)=>headers.map((header)=>csvCell(row[header])).join(','))
  ].join('\n');
  const blob=new Blob([csv],{type:'text/csv;charset=utf-8'});
  const url=URL.createObjectURL(blob);
  const link=document.createElement('a');

  link.href=url;
  link.download=name;
  link.click();
  URL.revokeObjectURL(url);
};

const amount=(order,key,fallback=0)=>Number(order?.[key] ?? fallback);

const centsValue=(value)=>{
  if (value===null || value===undefined) return null;

  const parsed=Number(value);
  return Number.isFinite(parsed) ? parsed/100 : null;
};

const taxAmount=(order)=>{
  if (
    order?.stripe_tax_amount_cents !== null
    && order?.stripe_tax_amount_cents !== undefined
  ) {
    return Number(order.stripe_tax_amount_cents) / 100;
  }

  const recordedTax=amount(order,'tax_amount');

  return recordedTax>0
    ? recordedTax
    : amount(order,'stripe_tax_amount');
};

const platformFeeAmount=(order)=>{
  if (
    order?.platform_commission_cents !== null
    && order?.platform_commission_cents !== undefined
  ) {
    return centsValue(order.platform_commission_cents);
  }

  const applicationFee=amount(order,'application_fee_amount');

  return applicationFee>0
    ? applicationFee
    : amount(order,'platform_fee');
};

const stripeFeeAmount=(order)=>{
  if (
    order?.stripe_processing_fee_cents !== null
    && order?.stripe_processing_fee_cents !== undefined
  ) {
    return centsValue(order.stripe_processing_fee_cents);
  }

  if (order?.stripe_fee !== null && order?.stripe_fee !== undefined) {
    return amount(order,'stripe_fee');
  }

  return null;
};

const totalPaidAmount=(order)=>{
  if (
    order?.stripe_amount_total_cents !== null
    && order?.stripe_amount_total_cents !== undefined
  ) {
    return centsValue(order.stripe_amount_total_cents);
  }

  return amount(order,'total_amount');
};

const productSubtotalAmount=(order,tax,totalPaid)=>{
  if (
    order?.product_subtotal_cents !== null
    && order?.product_subtotal_cents !== undefined
  ) {
    return centsValue(order.product_subtotal_cents);
  }

  return Math.max(
    totalPaid - tax - amount(order,'delivery_fee'),
    0
  );
};

const getFinanceRows=(orders=[])=>orders
  .filter((order)=>order?.payment_mode==='platform')
  .map((order)=>{
    const tax=taxAmount(order);
    const totalPaid=totalPaidAmount(order);
    const platformFee=platformFeeAmount(order);
    const stripeFee=stripeFeeAmount(order);
    const subtotal=productSubtotalAmount(order,tax || 0,totalPaid);
    const refundAmount=order.payment_status==='refunded'
      ? amount(order,'total_amount')
      : amount(order,'refund_amount');
    const normalizedTaxNumber=String(
      order.seller_gst_hst_number_snapshot || ''
    ).replace(/[^0-9]/g,'');
    const sellerTaxRegistered=order.seller_gst_hst_registered_snapshot===true
      && /^[0-9]{9}$/.test(normalizedTaxNumber);
    const sellerTax= sellerTaxRegistered
      && order.seller_tax_liable_party_snapshot==='seller'
      ? tax || 0
      : 0;

    return {
      id:order.id,
      order_id:order.id?.slice(0,8).toUpperCase(),
      customer:order.buyer?.name || 'Customer',
      date:order.created_at
        ? new Date(order.created_at).toLocaleDateString()
        : '—',
      order_status:order.status || 'pending',
      payment_status:order.payment_status || 'unpaid',
      payment_captured:order.payment_captured===true,
      refunded:order.payment_status==='refunded'
        || Boolean(order.refunded_at)
        || amount(order,'refund_amount')>0,
      subtotal,
      tax,
      total_paid:totalPaid,
      platform_commission:platformFee,
      stripe_fee:stripeFee,
      processing_fee:stripeFee,
      refund_amount:refundAmount,
      net_to_seller:subtotal===null
        ? null
        : Math.max(
          subtotal
          + sellerTax
          - platformFee
          - refundAmount,
          0
        ),
      payout_status:order.payment_status==='refunded' || order.refunded_at
        ? 'refunded'
        : !order.payment_captured
          ? 'not captured'
          : order.status==='completed'
            ? 'paid out'
            : 'pending'
    };
  });

const exportRows=(rows,type)=>rows.map((row)=>({
  order_id:row.order_id,
  customer:row.customer,
  date:row.date,
  order_status:row.order_status,
  payment_status:row.payment_status,
  subtotal:row.subtotal.toFixed(2),
  tax:row.tax.toFixed(2),
  total_paid:row.total_paid.toFixed(2),
  platform_commission:row.platform_commission.toFixed(2),
  'Stripe Fee':row.stripe_fee===null
    ? 'Pending calculation'
    : row.stripe_fee.toFixed(2),
  refund_amount:row.refund_amount.toFixed(2),
  net_to_seller:row.net_to_seller===null
    ? 'Pending calculation'
    : row.net_to_seller.toFixed(2),
  payout_status:row.payout_status,
  export_type:type
}));

const FinanceCard=({label,value,icon,accent='text-indigo-600'})=>(
  <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
    <SafeIcon icon={icon} className={`mb-4 text-xl ${accent}`} />
    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
      {label}
    </p>
    <p className="mt-2 text-2xl font-black text-slate-900">{money(value)}</p>
  </div>
);

const SellerFinanceTab=({seller,orders=[]})=>{
  const [loadingStripe,setLoadingStripe]=useState(false);
  const [error,setError]=useState('');
  const [page,setPage]=useState(1);
  const [financeOrders,setFinanceOrders]=useState([]);
  const [orderFilter,setOrderFilter]=useState('active');
  const [paymentFilter,setPaymentFilter]=useState('captured');
  const reconciliationAttempts=useRef(new Set());

  useEffect(()=>{
    if (!seller?.id) {
      setFinanceOrders([]);
      return undefined;
    }

    let active=true;

    const loadFinanceOrders=async()=>{
      const {data,error:requestError}=await supabase
        .from('orders')
        .select('*,buyer:users!orders_buyer_id_fkey(name)')
        .eq('seller_id',seller.id)
        .eq('payment_mode','platform')
        .order('created_at',{ascending:false});

      if (!active) return;

      if (requestError) {
        console.error(
          'Seller finance order refresh failed:',
          requestError
        );
        return;
      }

      setFinanceOrders(data || []);
    };

    loadFinanceOrders();

    const channel=supabase
      .channel(`seller-finance-orders-${seller.id}`)
      .on(
        'postgres_changes',
        {
          event:'*',
          schema:'public',
          table:'orders',
          filter:`seller_id=eq.${seller.id}`
        },
        loadFinanceOrders
      )
      .subscribe();

    return ()=>{
      active=false;
      supabase.removeChannel(channel);
    };
  },[seller?.id]);

  useEffect(()=>{
    if (!seller?.id || !financeOrders.length) return undefined;

    let active=true;
    const missingFees=financeOrders
      .filter((order)=>(
        order.payment_mode==='platform'
        && order.payment_captured===true
        && order.stripe_processing_fee_cents==null
        && order.stripe_fee==null
        && order.stripe_payment_intent_id
        && !reconciliationAttempts.current.has(order.id)
      ))
      .slice(0,20);

    if (!missingFees.length) return undefined;

    missingFees.forEach((order)=>reconciliationAttempts.current.add(order.id));

    const reconcileFees=async()=>{
      await Promise.allSettled(missingFees.map((order)=>(
        supabase.functions.invoke(
          'stripe-order-finance-reconcile',
          {body:{order_id:order.id}}
        )
      )));

      if (!active) return;

      const {data,error:requestError}=await supabase
        .from('orders')
        .select('*,buyer:users!orders_buyer_id_fkey(name)')
        .eq('seller_id',seller.id)
        .eq('payment_mode','platform')
        .order('created_at',{ascending:false});

      if (!requestError && active) {
        setFinanceOrders(data || []);
      }
    };

    reconcileFees();

    return ()=>{
      active=false;
    };
  },[seller?.id,financeOrders]);

  const rows=useMemo(()=>{
    const orderMap=new Map(
      orders.map((order)=>[order.id,order])
    );

    financeOrders.forEach((order)=>{
      const existing=orderMap.get(order.id);
      orderMap.set(order.id,{
        ...existing,
        ...order,
        buyer:order.buyer || existing?.buyer
      });
    });

    const sortedOrders=[...orderMap.values()].sort(
      (first,second)=>new Date(second.created_at)-new Date(first.created_at)
    );

    return getFinanceRows(sortedOrders).filter((row)=>{
      const orderMatches=orderFilter==='all'
        || !['cancelled','declined'].includes(row.order_status);
      const paymentMatches=paymentFilter==='all'
        || (
          paymentFilter==='captured'
          && row.payment_captured
        )
        || (
          paymentFilter==='not-captured'
          && !row.payment_captured
          && !row.refunded
        )
        || (
          paymentFilter==='refunded'
          && row.refunded
        );

      return orderMatches && paymentMatches;
    });
  },[orders,financeOrders,orderFilter,paymentFilter]);

  const pageCount=Math.max(1,Math.ceil(rows.length/PAGE_SIZE));
  const currentPage=Math.min(page,pageCount);
  const startIndex=(currentPage-1)*PAGE_SIZE;
  const visibleRows=rows.slice(startIndex,startIndex+PAGE_SIZE);

  useEffect(()=>{
    setPage(1);
  },[rows.length]);

  const totals=useMemo(()=>rows.reduce((result,row)=>{
    result.gross+=row.total_paid;
    result.tax+=row.tax;
    result.fees+=row.processing_fee || 0;
    result.platformCommission+=row.platform_commission;
    result.refunds+=row.refund_amount;
    result.net+=row.net_to_seller || 0;

    if (row.payout_status==='pending' && row.net_to_seller!==null) {
      result.pending+=row.net_to_seller;
    }
    if (row.payout_status==='paid out' && row.net_to_seller!==null) {
      result.paid+=row.net_to_seller;
    }
    if (row.payment_status==='failed') result.failed+=1;

    return result;
  },{
    gross:0,
    tax:0,
    fees:0,
    platformCommission:0,
    refunds:0,
    net:0,
    pending:0,
    paid:0,
    failed:0
  }),[rows]);

  const stripeStatus=seller?.stripe_onboarding_complete
    ? 'Connected and verified'
    : seller?.stripe_account_id
      ? 'Action required'
      : 'Not connected';

  const handleStripeAccount=async()=>{
    setLoadingStripe(true);
    setError('');

    try {
      const result=await startStripeSellerOnboarding(
        seller.id,
        '/seller-dashboard'
      );

      if (!result?.url) {
        throw new Error('Stripe did not return an account update link.');
      }

      window.location.assign(result.url);
    } catch (stripeError) {
      setError(stripeError.message || 'Unable to open your Stripe account.');
      setLoadingStripe(false);
    }
  };

  const exportFile=(type)=>{
    downloadCsv(
      `sohlocal-seller-${type}.csv`,
      exportRows(rows,type)
    );
  };

  return (
    <div className="space-y-8">
      {error && (
        <div className="flex items-center rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
          <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" />
          {error}
        </div>
      )}

      <div>
        <h2 className="text-4xl font-black tracking-tighter text-slate-900">
          Seller <span className="text-indigo-600">Finance</span>
        </h2>
        <p className="mt-2 text-sm font-medium text-slate-500">
          Track your SOHLocal payments, payouts, and tax reporting.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <FinanceCard label="Gross sales" value={totals.gross} icon={FiTrendingUp} />
        <FinanceCard label="Taxes collected" value={totals.tax} icon={FiShield} />
        <FinanceCard
          label="Stripe Fee"
          value={totals.fees}
          icon={FiCreditCard}
          accent="text-rose-500"
        />
        <FinanceCard label="SOHLOCAL Fee" value={totals.platformCommission} icon={FiDollarSign} />
        <FinanceCard label="Refunds" value={totals.refunds} icon={FiRefreshCw} accent="text-amber-500" />
        <FinanceCard label="Net earnings" value={totals.net} icon={FiDollarSign} accent="text-emerald-600" />
        <FinanceCard label="Pending payout" value={totals.pending} icon={FiLoader} accent="text-blue-600" />
        <FinanceCard label="Paid out total" value={totals.paid} icon={FiCheckCircle} accent="text-emerald-600" />
        <FinanceCard label="Failed payouts" value={totals.failed} icon={FiXCircle} accent="text-red-500" />
      </div>

      <div className="rounded-[2rem] border border-indigo-100 bg-indigo-50 p-6">
        <div className="flex items-start">
          <SafeIcon icon={FiInfo} className="mr-3 mt-1 text-xl text-indigo-600" />
          <p className="text-sm font-medium leading-relaxed text-indigo-900">
            Online payments include a processing fee to cover payment provider costs. This fee is deducted from your payout when a customer pays through SOHLocal.
          </p>
        </div>
      </div>

      <section className="rounded-[2rem] border border-slate-100 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col justify-between gap-5 md:flex-row md:items-center">
          <div>
            <h3 className="text-2xl font-black text-slate-900">Payouts</h3>
            <p className="mt-1 text-sm font-medium text-slate-500">
              Your Stripe-connected payout account
            </p>
          </div>
          <button
            onClick={handleStripeAccount}
            disabled={loadingStripe || !seller?.id}
            className="flex items-center justify-center rounded-2xl bg-indigo-600 px-6 py-4 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
          >
            {loadingStripe
              ? <SafeIcon icon={FiLoader} className="mr-2 animate-spin" />
              : <SafeIcon icon={FiExternalLink} className="mr-2" />}
            Update Stripe Account
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[
            ['Available balance',totals.paid],
            ['Pending balance',totals.pending],
            ['Next payout estimate',totals.pending],
            ['Last payout',totals.paid],
            ['Failed payouts',totals.failed]
          ].map(([label,value])=>(
            <div key={label} className="rounded-2xl bg-slate-50 p-5">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {label}
              </p>
              <p className="mt-2 text-xl font-black text-slate-900">{money(value)}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 flex items-center rounded-2xl border border-slate-100 px-5 py-4">
          <SafeIcon
            icon={seller?.stripe_onboarding_complete ? FiCheckCircle : FiAlertCircle}
            className={`mr-3 text-xl ${seller?.stripe_onboarding_complete ? 'text-emerald-600' : 'text-amber-500'}`}
          />
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Stripe account status
            </p>
            <p className="mt-1 text-sm font-black text-slate-900">{stripeStatus}</p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-5 border-b border-slate-100 p-8 md:flex-row md:items-center">
          <div>
            <h3 className="text-2xl font-black text-slate-900">Orders and payments</h3>
            <p className="mt-1 text-sm font-medium text-slate-500">
              Only your SOHLocal-processed seller orders are shown.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ['orders','Orders'],
              ['payouts','Payouts'],
              ['fees','Fees'],
              ['refunds','Refunds'],
              ['tax-summaries','Tax summaries']
            ].map(([type,label])=>(
              <button
                key={type}
                onClick={()=>exportFile(type)}
                className="flex items-center rounded-xl border border-slate-200 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:border-indigo-300 hover:text-indigo-600"
              >
                <SafeIcon icon={FiDownload} className="mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 border-b border-slate-100 px-8 py-5">
          <label className="flex flex-col gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
            Order filter
            <select
              value={orderFilter}
              onChange={(event)=>setOrderFilter(event.target.value)}
              className="min-w-52 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-bold normal-case tracking-normal text-slate-700"
            >
              <option value="active">Exclude cancelled and declined</option>
              <option value="all">All orders</option>
            </select>
          </label>
          <label className="flex flex-col gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
            Payment filter
            <select
              value={paymentFilter}
              onChange={(event)=>setPaymentFilter(event.target.value)}
              className="min-w-52 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-bold normal-case tracking-normal text-slate-700"
            >
              <option value="captured">Captured payments only</option>
              <option value="not-captured">Not captured</option>
              <option value="refunded">Refunded payments</option>
              <option value="all">All payment states</option>
            </select>
          </label>
          <p className="self-end pb-3 text-xs font-medium text-slate-500">
            Filters apply to the totals, table, and CSV exports.
          </p>
        </div>

        <div
          className="overflow-x-auto"
          style={{
            maxHeight:'none',
            overflowY:'visible',
            scrollbarGutter:'auto'
          }}
        >
          <table className="w-full min-w-[1450px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
                {[
                  'Order ID','Customer','Date','Order status','Payment status',
                  'Subtotal','Tax','Total paid','Stripe Fee','SOHLOCAL Fee',
                  'Refund amount','Net to seller','Payout status'
                ].map((heading)=>(
                  <th key={heading} className="px-5 py-4">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visibleRows.map((row)=>(
                <tr key={row.id} className="text-sm">
                  <td className="px-5 py-5 font-black text-indigo-600">{row.order_id}</td>
                  <td className="px-5 py-5 font-bold text-slate-900">{row.customer}</td>
                  <td className="px-5 py-5 text-slate-500">{row.date}</td>
                  <td className="px-5 py-5 text-xs font-black uppercase">{row.order_status}</td>
                  <td className="px-5 py-5 text-xs font-black uppercase">{row.payment_status}</td>
                  <td className="px-5 py-5">{money(row.subtotal)}</td>
                  <td className="px-5 py-5">{money(row.tax)}</td>
                  <td className="px-5 py-5 font-black">{money(row.total_paid)}</td>
                  <td className="px-5 py-5 text-rose-500">
                    {row.stripe_fee===null
                      ? 'Pending calculation'
                      : money(row.stripe_fee)}
                  </td>
                  <td className="px-5 py-5 text-indigo-600">{money(row.platform_commission)}</td>
                  <td className="px-5 py-5 text-amber-600">{money(row.refund_amount)}</td>
                  <td className="px-5 py-5 font-black text-emerald-600">{money(row.net_to_seller)}</td>
                  <td className="px-5 py-5 text-xs font-black uppercase">{row.payout_status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {!rows.length && (
            <div className="p-16 text-center text-sm font-bold text-slate-400">
              No platform-processed orders are available yet.
            </div>
          )}
        </div>

        {rows.length>PAGE_SIZE && (
          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 px-6 py-4 sm:flex-row">
            <p className="text-xs font-bold text-slate-500">
              Showing {startIndex+1}–{Math.min(startIndex+PAGE_SIZE,rows.length)} of {rows.length} payments
            </p>
            <nav className="flex items-center gap-2" aria-label="Finance table pages">
              <button
                type="button"
                onClick={()=>setPage((currentPage)=>Math.max(1,currentPage-1))}
                disabled={currentPage===1}
                aria-label="Show previous payments"
                className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-black text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="min-w-20 text-center text-xs font-black text-slate-500">
                Page {currentPage} of {pageCount}
              </span>
              <button
                type="button"
                onClick={()=>setPage((currentPage)=>Math.min(pageCount,currentPage+1))}
                disabled={currentPage===pageCount}
                aria-label="Show next payments"
                className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-black text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </section>

      <SellerDisputesPanel seller={seller} />
    </div>
  );
};

export default SellerFinanceTab;
